import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getUnderground, preloadUnderground } from '../underground-assets.js';

// Underground - a rave in a disused railway tunnel, from TWO photographs of
// the same tunnel: its LED tubes off, and on. Techno, hard techno, EBM,
// drum'n'bass.
//
// THE IDEA. A tunnel in central perspective is a speed machine. All the
// tubes at one depth lit together make a RING; a ring that moves from one
// depth to the next rushes at the viewer. The tunnel swallows the camera
// without the camera moving.
//
// NeonAlley's render, one line per pixel:
//     colour = off + diff * intensity(tube) * colour(tube)
// with tools/build-underground.mjs's id map saying which tube every pixel's
// light belongs to (cores, lit concrete, puddle reflections) and a 128-entry
// table written by the CPU every frame. The build also gives every tube its
// RING (a station of the tunnel: a ceiling arc and the two wall columns at
// one depth - seven in this picture) and its angle around the vanishing
// point, which is all the choreography below needs.
//
// The tubes are white in the picture: the colour multiplies the light, and
// the core keeps its luminance for the bloom (mixed back toward white where
// diff is bright). Attack 20 ms, release 120 ms: nervous fluorescents.

// Shared-rig contract: no rig at all, the picture is its own lighting.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'underground',
  family: 'photographic',
  danceability: 0.88,
  energyLevel: 0.86,
  bpmMin: 120,
  bpmMax: 175,
  movement: 0.85,
  tonality: 0.35,
  prefers: { gridStrength: [0.65, 1.0], lowEnd: [0.50, 1.0] },
  enabled: true
};

const DEFAULT_TIER = 2;
const TIER = {
  2: { lasers: 4, laserHalo: true },
  1: { lasers: 4, laserHalo: false },
  0: { lasers: 0, laserHalo: false }
};
const TUBES_W = 128;           // the table, one texel per tube id
const SAFE_FLOOR = 0.5;        // the cover sits at the vanishing point
const MAX_INTENSITY = 1.5;

// --- the tubes --------------------------------------------------------------------
const ATTACK_S = 0.020, RELEASE_S = 0.120;
const SHIMMER = 0.04;          // x bands.high, at a fast aliased rate
const CORE_WHITE = 0.45;       // how much of the core's own white survives the colour

// --- the rings ---------------------------------------------------------------------
// A ring is a FRONT at a depth p (0 the far end, 1 the camera) with a trail
// behind it. The brief's trail of 3 ranks out of 24 is RING_TRAIL of the
// tunnel's depth; the picture has seven stations, so the trail bridges a
// little less than one station and the tubes' own release does the rest.
const RING_TRAIL = 0.125;
const RING_FRONT = 0.03;
const BAR_GAIN = 1.3;          // the bar's double ring: white, thicker
const BAR_TRAIL = 2.0;
const SPIRAL_SECTOR = 0.55;    // radians, half-width of the lit sector
const SPIRAL_TURNS = 1.0;      // turns per ring travel
const STROBE_S = 0.05;
const BREATH_LEVEL = 0.40;
const BREATH_BARS = 2;
const PATTERNS = ['rings', 'spiral', 'split', 'reverse', 'strobe'];
const BRIDGE_PATTERN = 'breath';
const DROP_DARK_BEATS = 1;
const DROP_WHITE_BEATS = 0.5;
const DROP_SPIRAL_BARS = 4;
const MONTEE_HALF_MS = 8 * 4;  // in beats: 8 bars - rings every half beat; 2 bars - every quarter
const MONTEE_QUARTER_MS = 2 * 4;

// --- comfort ---------------------------------------------------------------------------
const FLASHES_PER_S = 3;
const LUM_MAX = 0.45;
// the frame's mean luminance with everything off / everything on, through
// the bloomed chain (measured on the harness: 0.117 / 0.167 - the tone
// mapping compresses a lit tunnel hard), for the estimate the governor
// works from; it scales the targets when the 1 s average would exceed
// LUM_MAX. With these numbers the limit holds by construction (a strobe
// second measured 0.14, the drop's second 0.22); the governor stays as the
// guard against a brighter chain.
const LUM_OFF = 0.12, LUM_ON = 0.17;

// --- the lasers ----------------------------------------------------------------------
const LASER_MIN_ENERGY = 0.6;
const LASER_START = 0.27;      // of the frame height from the vanishing point: outside the cover
const LASER_BASE = [-0.42, 0.42, Math.PI - 0.42, Math.PI + 0.42];   // toward the four walls' corners
const LASER_SWEEP = 0.22;      // radians
const LASER_FAN = 0.55;        // the drop opens them
const LASER_PX = 1.4;
const LASER_GAIN = 0.55;

// --- the puddles ---------------------------------------------------------------------
const RIPPLE_PX = 2.0;

const BEATS_PER_PHRASE = 16;
const SYNTH_BPM = 128;

function rankPalette(count) {
  const pal = [[0, 0, 0]];
  for (let i = 1; i <= count; i++) { const h = (i * 0.618034) % 1, s = 0.8, v = 0.95; const k = (n) => { const kk = (n + h * 6) % 6; return v - v * s * Math.max(0, Math.min(kk, 4 - kk, 1)); }; pal.push([k(5), k(3), k(1)]); }
  return pal;
}
function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
function lerp(a, b, t) { return a + (b - a) * t; }

// =============================================================================
// SHADERS
// =============================================================================

const VERTEX = `
  varying vec2 vUv;
  varying vec4 vClip;
  void main() {
    vUv = uv;
    vec4 clip = vec4(position.xy, 0.0, 1.0);
    vClip = clip;
    gl_Position = clip;
  }
`;

// mediump throughout, highp where the id is decoded.
const FRAGMENT = `
  precision mediump float;
  uniform sampler2D uOff;
  uniform sampler2D uDiff;
  uniform highp sampler2D uIds;
  uniform sampler2D uTubes;       // 128 x 1: R intensity, GBA colour
  uniform float uMean;            // what a far halo follows
  uniform float uTime;
  uniform float uRipple;          // uv amplitude in the puddles
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  uniform float uIdDebug;
  uniform float uExact;
  varying vec2 vUv;
  varying vec4 vClip;
${SAFE_ZONE_VERTEX_CHUNK}

  void main() {
    vec2 uv = vUv * uUvScale + uUvOffset;
    highp vec4 idc = texture2D(uIds, uv);
    highp float id = floor(idc.r * 255.0 + 0.5) * 256.0 + floor(idc.g * 255.0 + 0.5);
    float w = idc.b;
    float refl = idc.a;
    vec4 td = texture2D(uTubes, vec2((id + 0.5) / ${TUBES_W}.0, 0.5));

    if (uIdDebug > 0.5) {
      float k = w > 0.999 ? 1.0 : 0.12 + 0.4 * w;
      vec3 c = td.gba * k + vec3(refl * 0.2);
      vec3 lo = c / 12.92;
      vec3 hi = pow((c + 0.055) / 1.055, vec3(2.4));
      gl_FragColor = vec4(mix(lo, hi, step(0.04045, c)), 1.0);
      return;
    }

    float I = mix(uMean, td.r, w);
    // the puddles live: a slow wave displaces where the reflection is read
    vec2 duv = vec2(0.0);
    if (refl > 0.5) duv = vec2(0.0, uRipple * (sin(uv.x * 55.0 + uTime * 2.1) * 0.6 + sin(uv.x * 23.0 - uTime * 1.3 + uv.y * 9.0) * 0.4));
    vec3 off = texture2D(uOff, uv).rgb;
    vec3 diff = texture2D(uDiff, uv + duv).rgb;
    // the colour multiplies the light; the core keeps its white for the bloom
    float core = smoothstep(0.35, 0.85, dot(diff, vec3(0.2126, 0.7152, 0.0722)));
    vec3 tint = mix(td.gba, vec3(1.0), core * ${CORE_WHITE.toFixed(2)});
    vec3 col = off + diff * I * tint;

    float att = safeZoneAttenuation(vClip);
    att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((att - 0.32) / 0.68, 0.0, 1.0));
    att = mix(att, 1.0, uExact);
    gl_FragColor = vec4(col * att, 1.0);
  }
`;

// THE LASERS: four lines from the vanishing point to the frame's edges,
// each a thin QUAD along its line (instanced, additive) - a full-screen pass
// evaluating the four lines per pixel cost 18 ms on the Pi, the quads cover
// a few percent of the frame. Brighter toward the far end (the haze
// thickens there), nothing inside LASER_START of the vanishing point: they
// converge outside the cover.
const LASER_HALF = 0.035;      // the quad's half-width, frame-height units (12 px at 720)
const LASER_LEN = 3.2;         // past every frame edge; the viewport clips the rest
const LASER_VERTEX = `
  attribute float aLaser;
  uniform vec2 uVp;               // ndc-like: x scaled by aspect (uAspect comes with the safe-zone chunk)
  uniform vec4 uAngles;
  varying vec2 vLine;             // x along the line, y across (frame-height units)
  varying float vLaser;
  varying vec4 vClip;
${SAFE_ZONE_VERTEX_CHUNK}
  void main() {
    float ang = aLaser < 0.5 ? uAngles.x : (aLaser < 1.5 ? uAngles.y : (aLaser < 2.5 ? uAngles.z : uAngles.w));
    vec2 d = vec2(cos(ang), sin(ang)), n = vec2(-d.y, d.x);
    float along = ${LASER_START.toFixed(2)} + (uv.x) * ${LASER_LEN.toFixed(1)};
    float across = (uv.y - 0.5) * 2.0 * ${LASER_HALF.toFixed(3)};
    vec2 p = uVp + d * along + n * across;
    vec4 clip = vec4(p.x / uAspect, p.y, 0.0, 1.0);
    vLine = vec2(along, across);
    vLaser = aLaser;
    vClip = clip;
    gl_Position = clip;
  }
`;
// highp: uAspect is highp in the vertex stage (the safe-zone chunk), and a
// uniform's precision must agree between the two.
const LASER_FRAGMENT = `
  precision highp float;
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform float uGain;
  uniform float uPx;              // one pixel, in frame-height units
  uniform float uHalo;
  varying vec2 vLine;
  varying float vLaser;
  varying vec4 vClip;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  void main() {
    float dist = abs(vLine.y), along = vLine.x;
    float core = exp(-dist * dist / (2.0 * uPx * uPx * ${(LASER_PX * LASER_PX).toFixed(2)}));
    float halo = exp(-dist / (uPx * 9.0)) * 0.25 * uHalo;
    float fog = 0.35 + 0.65 * exp(-(along - ${LASER_START.toFixed(2)}) * 1.6);
    float fade = smoothstep(${LASER_START.toFixed(2)}, ${(LASER_START + 0.12).toFixed(2)}, along);
    vec3 colour = mod(vLaser, 2.0) < 0.5 ? uColorA : uColorB;
    vec2 ndc = vClip.xy / max(vClip.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float dd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, dd));
    gl_FragColor = vec4(colour * (core + halo) * fog * fade * uGain * att, 1.0);
  }
`;

export class UndergroundVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    // ?ugid=1 colours the tubes by ring; ?ugrank=<n> lights one ring with its
    // reflections; ?ugpattern=<name> pins a pattern; ?ugall=<f> pins every
    // tube; ?ugprobe=1 publishes the state on window.__underground.
    this._idDebug = urlParam('ugid') === '1';
    const rank = urlParam('ugrank');
    this._rankDebug = rank != null && rank !== '' && Number.isFinite(parseInt(rank, 10)) ? parseInt(rank, 10) : null;
    const pat = urlParam('ugpattern');
    this._forcedPattern = pat && (PATTERNS.indexOf(pat) >= 0 || pat === BRIDGE_PATTERN) ? pat : null;
    const all = urlParam('ugall');
    this._allDebug = all != null && all !== '' && Number.isFinite(parseFloat(all)) ? parseFloat(all) : null;
    this._probe = urlParam('ugprobe') === '1';
    const exact = this._idDebug || this._rankDebug != null || this._allDebug != null;
    this._exact = exact;
    this._postProcessing = exact
      ? { bloomStrength: 0, vignette: 0, grain: 0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }
      : { bloomStrength: 0.55, vignette: 0.25, aberration: 0.0015 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const assets = getUnderground();
    this._assets = assets;
    if (!assets) {
      const self = this;
      preloadUnderground().then(function (a) {
        if (!a || !self._scene || self._assets) return;
        const s = self._scene, c = self._camera;
        self.dispose();
        self.init(s, c);
      });
    }
    const data = assets ? assets.data : { count: 0, ranks: 1, vp: [0.5, 0.5], size: [1376, 768], tubes: [] };
    this._count = data.count;
    this._ranks = Math.max(1, data.ranks);
    this._vp = data.vp;
    this._imageAspect = data.size[0] / data.size[1];
    this._tubes = [];
    for (const t of data.tubes) {
      this._tubes.push({ id: t.id, rank: t.rank, depth: this._ranks > 1 ? t.rank / (this._ranks - 1) : 0, angle: t.angle, side: t.side, cx: t.cx, cy: t.cy, lit: 0, target: 0, shimmer: Math.random() * 6.28 });
    }

    // --- the table
    this._tubesData = new Float32Array(TUBES_W * 4);
    this._rankPal = rankPalette(this._ranks);
    this._tubesTex = new THREE.DataTexture(this._tubesData, TUBES_W, 1, THREE.RGBAFormat, THREE.FloatType);
    this._tubesTex.minFilter = THREE.NearestFilter;
    this._tubesTex.magFilter = THREE.NearestFilter;
    this._tubesTex.generateMipmaps = false;
    this._tubesTex.needsUpdate = true;

    this._uniforms = Object.assign({
      uOff: { value: assets ? assets.off : null },
      uDiff: { value: assets ? assets.diff : null },
      uIds: { value: assets ? assets.ids : null },
      uTubes: { value: this._tubesTex },
      uMean: { value: 0 },
      uTime: { value: 0 },
      uRipple: { value: this._exact ? 0 : RIPPLE_PX / data.size[1] },
      uUvScale: { value: new THREE.Vector2(1, 1) },
      uUvOffset: { value: new THREE.Vector2(0, 0) },
      uIdDebug: { value: this._idDebug ? 1 : 0 },
      uExact: { value: this._exact ? 1 : 0 }
    }, safeZoneUniforms);
    this._material = new THREE.ShaderMaterial({ uniforms: this._uniforms, vertexShader: VERTEX, fragmentShader: FRAGMENT, depthTest: false, depthWrite: false });
    this._geometry = new THREE.PlaneGeometry(2, 2);
    this._quad = new THREE.Mesh(this._geometry, this._material);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -10;
    scene.add(this._quad);

    // --- the lasers
    this._laserUniforms = Object.assign({
      uVp: { value: new THREE.Vector2(0, 0) }, uAngles: { value: new THREE.Vector4(...LASER_BASE) },
      uColorA: { value: new THREE.Color(0xff2fb0) }, uColorB: { value: new THREE.Color(0x2fd8ff) },
      uGain: { value: 0 }, uPx: { value: 2 / 720 }, uHalo: { value: this._layout.laserHalo ? 1 : 0 }, uCount: { value: this._layout.lasers }
    }, safeZoneUniforms);
    this._laserMaterial = new THREE.ShaderMaterial({ uniforms: this._laserUniforms, vertexShader: LASER_VERTEX, fragmentShader: LASER_FRAGMENT, blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    const lg = new THREE.InstancedBufferGeometry();
    const unit = new THREE.PlaneGeometry(1, 1);
    lg.setAttribute('position', unit.attributes.position);
    lg.setAttribute('uv', unit.attributes.uv);
    lg.setIndex(unit.index);
    lg.setAttribute('aLaser', new THREE.InstancedBufferAttribute(new Float32Array([0, 1, 2, 3]), 1));
    lg.instanceCount = this._layout.lasers;
    lg.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 10);
    this._laserGeometry = lg;
    this._laserUnit = unit;
    this._laserQuad = new THREE.Mesh(lg, this._laserMaterial);
    this._laserQuad.frustumCulled = false;
    this._laserQuad.renderOrder = -9;
    this._laserQuad.visible = this._layout.lasers > 0 && !this._exact;
    scene.add(this._laserQuad);

    // --- the behaviour
    this._time = 0;
    this._sinceStep = 0;
    this._step = 0;
    this._beatPeriod = 60 / SYNTH_BPM;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrasePhase = null;
    this._rings = [];
    this._patternIndex = 0;
    this._pattern = this._forcedPattern || PATTERNS[0];
    this._bridge = false;
    this._colorIndex = 0;
    this._ringParity = 0;
    this._palette = [new THREE.Color(0xff2fb0), new THREE.Color(0x2fd8ff), new THREE.Color(0xffb02f)];
    this._lastPaletteRef = undefined;
    this._strobeUntil = -1;
    this._flashTimes = [];
    this._flashesSkipped = 0;
    this._dropAt = -1e9;
    this._dropPhase = 0;         // 1 dark, 2 white, 3 spiral
    this._lastDropKey = null;
    this._lastMode = null;
    this._montee = 0;            // 0, 1 (half beats), 2 (quarters)
    this._subBeatAt = 0;
    this._energy = 0.5;
    this._laserGain = 0;
    this._laserFan = 0;
    this._lumAvg = LUM_OFF;
    this._lumScale = 1;
    this._bands = { low: 0.5, mid: 0.5, high: 0.5 };
    this._aspect = 0;
    this._writeTable(0);
    this._reframe();
  }

  _reframe() {
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    this._aspect = aspect;
    const u = this._uniforms;
    let scale, offset;
    if (aspect > this._imageAspect) { const k = this._imageAspect / aspect; scale = [1, k]; offset = [0, (1 - k) / 2]; }
    else { const k = aspect / this._imageAspect; scale = [k, 1]; offset = [(1 - k) / 2, 0]; }
    u.uUvScale.value.set(scale[0], scale[1]);
    u.uUvOffset.value.set(offset[0], offset[1]);
    // the vanishing point on the frame: image uv (y down) -> frame uv (y up) -> ndc with x scaled
    const fx = (this._vp[0] - offset[0]) / scale[0], fy = (1 - this._vp[1] - offset[1]) / scale[1];
    this._laserUniforms.uVp.value.set((fx * 2 - 1) * aspect, fy * 2 - 1);
    const h = typeof window !== 'undefined' ? window.innerHeight * Math.min(window.devicePixelRatio || 1, 2) : 720;
    this._laserUniforms.uPx.value = 2 / h;
  }

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length) ? palette.vividColors : [0xff2fb0, 0x2fd8ff, 0xffb02f];
    for (let i = 0; i < 3; i++) this._palette[i].set(vivid[i % vivid.length]);
    this._laserUniforms.uColorA.value.copy(this._palette[0]);
    this._laserUniforms.uColorB.value.copy(this._palette[1]);
  }

  // --- the rings --------------------------------------------------------------------
  // dir +1: born at the far end, rushing at the camera; -1: the reverse.
  _spawnRing(opts) {
    const o = opts || {};
    const colour = o.white ? null : this._palette[(this._colorIndex + (this._ringParity++ & 1)) % 3];
    this._rings.push({ t0: this._time, beats: o.beats || 1, dir: o.dir || 1, gain: o.gain || 1, white: !!o.white, trail: RING_TRAIL * (o.trail || 1), sides: o.sides || null, sector: o.sector != null ? o.sector : null, colour: colour });
    if (this._rings.length > 16) this._rings.shift();
  }

  _fullFlash() {
    // never more than FLASHES_PER_S full-tunnel flashes in any second
    const t = this._time;
    this._flashTimes = this._flashTimes.filter((x) => t - x < 1);
    if (this._flashTimes.length >= FLASHES_PER_S) { this._flashesSkipped++; return false; }
    this._flashTimes.push(t);
    this._strobeUntil = t + STROBE_S;
    return true;
  }

  _onBeat() {
    this._step++;
    this._sinceStep = 0;
    const bar = this._step % 4 === 0;
    if (this._dropPhase === 1 || this._dropPhase === 2) return;
    const pat = this._pattern;
    if (pat === 'strobe') { this._fullFlash(); return; }
    if (pat === 'breath') return;
    const gain = 0.6 + 0.6 * this._bands.low;
    const barRing = bar ? { white: true, gain: BAR_GAIN * gain, trail: BAR_TRAIL } : { gain: gain };
    if (pat === 'rings') this._spawnRing(barRing);
    else if (pat === 'reverse') this._spawnRing(Object.assign({ dir: -1 }, barRing));
    else if (pat === 'spiral') this._spawnRing(Object.assign({ sector: (this._step * 1.9) % (Math.PI * 2) }, barRing));
    else if (pat === 'split') this._spawnRing(Object.assign({ sides: 'C' }, barRing));
  }

  _onHalfBeat() {
    if (this._dropPhase === 1 || this._dropPhase === 2) return;
    const gain = 0.6 + 0.6 * this._bands.low;
    if (this._pattern === 'split') this._spawnRing({ sides: 'LR', gain: gain });
    else if (this._montee >= 1 && (this._pattern === 'rings' || this._pattern === 'spiral')) this._spawnRing({ gain: gain * 0.8, sector: this._pattern === 'spiral' ? (this._step * 1.9 + 0.95) % (Math.PI * 2) : null });
  }

  _onQuarterBeat() {
    if (this._montee >= 2 && (this._pattern === 'rings' || this._pattern === 'spiral') && this._dropPhase === 0) this._spawnRing({ gain: (0.6 + 0.6 * this._bands.low) * 0.6 });
  }

  _onBar() {}

  _onPhrase() {
    if (this._forcedPattern || this._dropPhase) return;
    this._patternIndex = (this._patternIndex + 1) % PATTERNS.length;
    this._pattern = this._bridge ? BRIDGE_PATTERN : PATTERNS[this._patternIndex];
    this._colorIndex = (this._colorIndex + 1) % 3;
  }

  _onDrop() {
    this._dropAt = this._time;
    this._dropPhase = 1;
    this._rings.length = 0;
    this._laserFan = 1;
  }

  // The target of every tube from the rings alive, then attack/release.
  _writeTable(dt) {
    const data = this._tubesData, t = this._time, beat = this._beatPeriod;
    const ka = 1 - Math.exp(-dt / ATTACK_S), kr = 1 - Math.exp(-dt / RELEASE_S);
    // retire the rings that left the tunnel
    this._rings = this._rings.filter((r) => (t - r.t0) / (r.beats * beat) < 1 + r.trail * 1.2 + 0.2);
    const strobe = t < this._strobeUntil;
    const breathLevel = BREATH_LEVEL * (0.8 + 0.2 * Math.sin(t / (BREATH_BARS * 4 * beat) * Math.PI * 2));
    let sum = 0, n = 0;
    const colour = new THREE.Color();
    for (const T of this._tubes) {
      let target = 0;
      colour.copy(this._palette[this._colorIndex]);
      if (this._allDebug != null) target = this._allDebug;
      else if (this._rankDebug != null) target = T.rank === this._rankDebug ? 1 : 0;
      else if (this._dropPhase === 1) target = 0;
      else if (this._dropPhase === 2 || strobe) { target = 1; colour.set(1, 1, 1); }
      else if (this._pattern === 'breath') target = breathLevel;
      else {
        let best = 0;
        for (const r of this._rings) {
          if (r.sides === 'C' && T.side !== 'C') continue;
          if (r.sides === 'LR' && T.side === 'C') continue;
          const p = (t - r.t0) / (r.beats * beat);                 // 0..1 along the travel
          const front = r.dir > 0 ? p : 1 - p;                       // depth of the front
          const behind = front - T.depth;                            // > 0: the front passed this tube
          let v = 0;
          if (behind >= -RING_FRONT && behind <= r.trail) v = behind < 0 ? 1 + behind / RING_FRONT : 1 - behind / r.trail;
          if (r.dir < 0) { const b2 = T.depth - front; v = 0; if (b2 >= -RING_FRONT && b2 <= r.trail) v = b2 < 0 ? 1 + b2 / RING_FRONT : 1 - b2 / r.trail; }
          if (r.sector != null) {
            const ang = r.sector + p * Math.PI * 2 * SPIRAL_TURNS;
            let d = Math.abs(((T.angle - ang) % (Math.PI * 2) + Math.PI * 3) % (Math.PI * 2) - Math.PI);
            v *= d < SPIRAL_SECTOR ? 1 : 0;
          }
          v *= r.gain;
          if (v > best) { best = v; if (r.white) colour.set(1, 1, 1); else if (r.colour) colour.copy(r.colour); }
        }
        target = best;
      }
      target *= this._lumScale;
      if (!this._exact) {
        T.shimmer = (T.shimmer + dt * 37) % 6.28;
        target *= 1 + SHIMMER * this._bands.high * Math.sin(T.shimmer);
      }
      T.lit += (target - T.lit) * (target > T.lit ? ka : kr);
      const v = Math.max(0, Math.min(MAX_INTENSITY, T.lit));
      data[T.id * 4] = v;
      if (this._idDebug) { const c = this._rankPal[T.rank + 1]; data[T.id * 4 + 1] = c[0]; data[T.id * 4 + 2] = c[1]; data[T.id * 4 + 3] = c[2]; }
      else { data[T.id * 4 + 1] = colour.r; data[T.id * 4 + 2] = colour.g; data[T.id * 4 + 3] = colour.b; }
      sum += v; n++;
    }
    data[0] = 0;
    this._tubesTex.needsUpdate = true;
    this._mean = n ? sum / n : 0;
    this._uniforms.uMean.value = this._rankDebug != null ? 0 : this._mean;
    // THE LUMINANCE GOVERNOR: the 1 s average of the estimated frame
    // luminance stays under LUM_MAX by scaling the targets
    const est = LUM_OFF + (LUM_ON - LUM_OFF) * Math.min(1, this._mean);
    this._lumAvg += (est - this._lumAvg) * Math.min(1, dt / 1.0);
    if (this._lumAvg > LUM_MAX) this._lumScale = Math.max(0.3, this._lumScale - dt * 0.8);
    else if (this._lumScale < 1) this._lumScale = Math.min(1, this._lumScale + dt * 0.3);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    this._sinceStep += dt;
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    const bands = director && director.hasReliableGrid && director.bands ? director.bands : null;
    const k = Math.min(1, dt / 0.15);
    this._bands.low += ((bands ? clamp01(bands.low) : 0.5) - this._bands.low) * k;
    this._bands.mid += ((bands ? clamp01(bands.mid) : 0.5) - this._bands.mid) * k;
    this._bands.high += ((bands ? clamp01(bands.high) : 0.5) - this._bands.high) * k;
    const tl = profile.analysisTimeline;
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    const e = tl && nowMs != null ? tl.lookupEnergy(nowMs) : null;
    this._energy = e != null ? e / 100 : (director && director.intensity != null ? director.intensity : 0.6);

    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    const aspect = safeZoneUniforms.uAspect.value || 0;
    if (aspect && aspect !== this._aspect) this._reframe();

    // pause: the tunnel breathes
    if (paused) {
      if (!this._forcedPattern) this._pattern = BRIDGE_PATTERN;
      this._writeTable(dt);
      this._uniforms.uTime.value = this._time % 64;
      this._laserUniforms.uGain.value = 0;
      return;
    }
    if (this._pattern === BRIDGE_PATTERN && !this._forcedPattern && !this._bridge) this._pattern = PATTERNS[this._patternIndex];

    // --- the bridge: breakdowns and intros breathe
    const mode = director ? director.mode : null;
    const bridge = mode === 'breakdown' || mode === 'intro' || mode === 'outro';
    if (bridge !== this._bridge && !this._forcedPattern && this._dropPhase === 0) {
      this._bridge = bridge;
      this._pattern = bridge ? BRIDGE_PATTERN : PATTERNS[this._patternIndex];
    }

    // --- the montee: a drop announced within 8 bars / 2 bars
    const ev = director && director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatMs = this._beatPeriod * 1000;
    this._montee = dropMs != null && dropMs > 0 ? (dropMs < MONTEE_QUARTER_MS * beatMs ? 2 : (dropMs < MONTEE_HALF_MS * beatMs ? 1 : 0)) : 0;

    // --- the drop, on the frame that crosses the mark
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey) { this._lastDropKey = key; this._onDrop(); }
    } else if (!tl && mode === 'drop' && this._lastMode !== 'drop') this._onDrop();
    this._lastMode = mode;
    if (this._dropPhase) {
      const age = (this._time - this._dropAt) / this._beatPeriod;
      if (this._dropPhase === 1 && age >= DROP_DARK_BEATS) { this._dropPhase = 2; this._fullFlash(); }
      if (this._dropPhase === 2 && age >= DROP_DARK_BEATS + DROP_WHITE_BEATS) { this._dropPhase = 3; this._pattern = this._forcedPattern || 'spiral'; }
      if (this._dropPhase === 3 && age >= DROP_DARK_BEATS + DROP_WHITE_BEATS + DROP_SPIRAL_BARS * 4) { this._dropPhase = 0; this._pattern = this._forcedPattern || PATTERNS[this._patternIndex]; }
    }

    // --- the clocks
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true;
      this._lastBeatPhase = beatPhase;
    } else if (this._sinceStep >= this._beatPeriod) beatEdge = true;
    if (beatEdge) { this._onBeat(); this._subBeatAt = this._time; this._subCount = 0; }
    // the sub-beats, from the last beat edge
    const sub = Math.floor((this._time - this._subBeatAt) / this._beatPeriod * 4);
    while ((this._subCount || 0) < sub && sub <= 3) {
      this._subCount = (this._subCount || 0) + 1;
      if (this._subCount === 2) this._onHalfBeat();
      else this._onQuarterBeat();
    }
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar(); this._lastBarPhase = barPhase; }
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) {
      if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase();
      this._lastPhrasePhase = phrasePhase;
    } else if (beatEdge && this._step > 0 && this._step % BEATS_PER_PHRASE === 0) this._onPhrase();

    this._writeTable(dt);
    this._uniforms.uTime.value = this._time % 64;

    // --- the lasers: energy over LASER_MIN_ENERGY, a slow sweep, the drop's fan
    const want = this._energy > LASER_MIN_ENERGY && !this._exact ? LASER_GAIN * (0.7 + 0.3 * this._bands.mid) : 0;
    this._laserGain += (want - this._laserGain) * Math.min(1, dt * 1.5);
    this._laserFan *= Math.exp(-dt / (DROP_SPIRAL_BARS * 4 * this._beatPeriod * 0.5));
    const a = this._laserUniforms.uAngles.value, t = this._time;
    for (let i = 0; i < 4; i++) {
      const base = LASER_BASE[i], sgn = i % 2 ? 1 : -1;
      a.setComponent(i, base + LASER_SWEEP * Math.sin(t * 0.35 + i * 1.7) + sgn * LASER_FAN * this._laserFan * (i < 2 ? 1 : -1));
    }
    this._laserUniforms.uGain.value = this._laserGain;
    if (this._probe && typeof window !== 'undefined') window.__underground = this.getProbe();
  }

  getProbe() {
    return {
      t: +this._time.toFixed(3), step: this._step, pattern: this._pattern, rings: this._rings.length, dropPhase: this._dropPhase, montee: this._montee,
      mean: +(this._mean || 0).toFixed(3), lumAvg: +this._lumAvg.toFixed(3), lumScale: +this._lumScale.toFixed(3), flashes1s: this._flashTimes.filter((x) => this._time - x < 1).length, flashesSkipped: this._flashesSkipped,
      energy: +this._energy.toFixed(2), laserGain: +this._laserGain.toFixed(3), fan: +this._laserFan.toFixed(3), ranks: this._ranks, tubes: this._tubes.length,
      byRank: Array.from({ length: this._ranks }, (_, r) => { const ts = this._tubes.filter((T) => T.rank === r); return ts.length ? +(ts.reduce((s, T) => s + T.lit, 0) / ts.length).toFixed(3) : 0; })
    };
  }

  dispose() {
    if (this._quad) this._scene.remove(this._quad);
    if (this._laserQuad) this._scene.remove(this._laserQuad);
    if (this._geometry) this._geometry.dispose();
    if (this._material) this._material.dispose();
    if (this._laserMaterial) this._laserMaterial.dispose();
    if (this._laserGeometry) this._laserGeometry.dispose();
    if (this._laserUnit) this._laserUnit.dispose();
    if (this._tubesTex) this._tubesTex.dispose();
    this._quad = null;
    if (typeof window !== 'undefined' && window.__underground) delete window.__underground;
  }
}
